import React, { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import isEqual from 'lodash/isEqual'

import UserContext from '../../context/User/User'
import LoadingContext from '../../context/Loading/Loading'
import FlexPackDetailsContext from '../../context/FlexPackDetails/FlexPackDetails'

import AuthService from '../../utilities/services/auth.service'

import useSafeAsync from '../../utilities/useSafeAsync'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import {
  getFlexPack,
  getSeasons,
  createOrEditFlexPack,
} from '../../utilities/api'

import {
  b64toBlob,
  checkPermission,
  getFormattedTimezoneDate,
  toUtcIsoFromTimezone,
  scrollToTop,
} from '../../utilities/helpers'

import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Alert from 'react-bootstrap/Alert'

import { BasicInfo } from './BasicInfo'
import { Details } from './Details'
import { Preview } from './Preview'
import PackageCustomFee from '../PackageDetailsWrapper/PackageCustomFee/PackageCustomFee'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { CreatePackageButtons } from '../CreatePackageButtons'

export default function FlexPackDetailsWrapper({ flexPackId }) {
  const navigate = useNavigate()

  const safeSetState = useSafeAsync()

  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)

  const {
    updateFlexPack,
    storeFlexPackStart,
    storeFlexPackEnd,
    updateCanPublish,
    updateIsFlexPackPublished,
    updateIsFlexPackOnsale,
    updateIsFlexPackSoldout,
    hasFlexPackEnded,
    updateHasFlexPackEnded,
    isFlexPackPublished,
  } = useContext(FlexPackDetailsContext)

  const { orgPermissions } = useContext(UserContext)

  const organization = AuthService.getOrg()[0]

  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.FLEX_PACKAGES_MANAGEMENT
    )

  const [showFooter, setShowFooter] = useState(false)

  const [seasons, setSeasons] = useState()

  const [initialState, setInitialState] = useState()

  const [flexPack, setFlexPack] = useState()

  const [color, setColor] = useState('#563d7c')

  const [image, setImage] = useState()

  const [coordinates, setCoordinates] = useState()

  const [start, setStart] = useState(
    new Date(moment('7:00 pm', 'h:mm a').format())
  )

  const [endTimeError, setEndTimeError] = useState(false)

  const [endTimeErrorMsg, setEndTimeErrorMsg] = useState('')

  const [end, setEnd] = useState(
    new Date(moment('11:00 pm', 'h:mm a').format())
  )

  const [isSaving, setIsSaving] = useState(false)

  // required fields error status
  const [requiredFieldErrorStatus, setRequiredFieldErrorStatus] = useState({})

  const [hasRequiredFieldError, setHasRequiredFieldError] = useState(false)

  const [isEditing, setIsEditing] = useState(false)

  const [alert, setAlert] = useState({
    show: false,
    variant: '',
    message: '',
  })

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }

    showLoading()
    loadFlexPack()
  }, [orgPermissionsReady, hasPermission, flexPackId])

  useEffect(() => {
    const hasChanges =
      !isEqual(initialState?.flexPack, flexPack) ||
      !isEqual(initialState?.image, image) ||
      !isEqual(new Date(initialState?.start), new Date(start)) ||
      !isEqual(initialState?.end, end)

    setShowFooter(hasChanges)
  }, [flexPack, initialState, image, start, end])

  useEffect(() => {
    // close all alerts when fields are edited and there is no required field error
    if (alert.show && !hasRequiredFieldError) {
      closeAlert()
    }
  }, [flexPack, image, start, end])

  useEffect(() => {
    if (hasRequiredFieldError) {
      // if all fields are filled in: reset required field state
      if (
        Object.keys(requiredFieldErrorStatus).every(
          (key) => !requiredFieldErrorStatus[key]
        )
      ) {
        setHasRequiredFieldError(false)
        setRequiredFieldErrorStatus({})
      }
    }
  }, [hasRequiredFieldError, requiredFieldErrorStatus])

  useEffect(() => {
    if (hasRequiredFieldError) {
      updateRequiredFieldErrorStatus()
    }
  }, [
    flexPack?.name,
    flexPack?.season,
    flexPack?.price,
    image,
    flexPack?.gameTickets,
  ])

  useEffect(() => {
    if (hasRequiredFieldError) {
      // if all fields are filled in: dismiss alert
      if (
        Object.keys(requiredFieldErrorStatus).every(
          (key) => !requiredFieldErrorStatus[key]
        )
      ) {
        closeAlert()
        setHasRequiredFieldError(false)
        setRequiredFieldErrorStatus({})
      }
    }
  }, [hasRequiredFieldError, requiredFieldErrorStatus])

  useEffect(() => {
    // validation for end date
    // - cannot be same/before start time
    setEndTimeError(isEndSameOrBeforeStart(end))
    setEndTimeErrorMsg(
      isEndSameOrBeforeStart(end) ? getIsEndSameOrBeforeErrorMsg : ''
    )
  }, [start, end])

  // end date cannot be same/before start time
  const isEndSameOrBeforeStart = (date) => {
    return moment(date)?.isSameOrBefore(moment(start))
  }

  // validation for end date
  // - cannot be same/before start time
  const getIsEndSameOrBeforeErrorMsg = () => {
    if (moment(end).isSameOrBefore(moment(start))) {
      return 'Time must be after start time'
    }
  }

  const loadFlexPack = async () => {
    try {
      const res = await getFlexPack(flexPackId)

      if (!res?.data) return

      const flexPackData = res?.data[0]

      safeSetState(() => {
        setFlexPack(flexPackData)
      })

      await new Promise(async (resolve) => {
        await loadSeasons()

        safeSetState(() => {
          setIsEditing(Boolean(flexPackData?.image?.url))

          // is editing existing flex pack
          if (flexPackData?.image?.url) {
            setImage(flexPackData?.image?.url)

            const formattedStart = new Date(
              getFormattedTimezoneDate(
                flexPackData?.start,
                flexPackData?.timezone
              )
            )
            setStart(formattedStart)

            const formattedEnd = new Date(
              getFormattedTimezoneDate(
                flexPackData?.end,
                flexPackData?.timezone
              )
            )
            setEnd(formattedEnd)

            saveInitialState(
              flexPackData,
              flexPackData?.image?.url,
              formattedStart,
              formattedEnd
            )
          } else {
            saveInitialState(flexPackData, image, start, end)
          }

          // Update context
          updateFlexPack(flexPackData)
          updateIsFlexPackOnsale(flexPackData)
          updateHasFlexPackEnded(flexPackData)
          updateIsFlexPackSoldout(flexPackData)
          updateIsFlexPackPublished(flexPackData)

          resolve() // Wait for this block to complete
        })
      })
    } catch (err) {
      console.error(err)
    } finally {
      hideLoading()
    }
  }

  const loadSeasons = async () => {
    try {
      const res = await getSeasons(organization.uuid)
      safeSetState(() => {
        setSeasons(res?.data)
      })
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  const handleRemove = () => {
    setImage()
    setCoordinates()
  }

  const handleChange = (e) => {
    if (e?.target) {
      const { name, value } = e.target
      setFlexPack((prevData) => ({
        ...prevData,
        [name]: value,
      }))
    }
  }

  const handleCustomFees = (customFees) => {
    setFlexPack((prevData) => ({
      ...prevData,
      customFees,
    }))
  }

  const handleSeason = (e) => {
    const id = Number(e.target.value)
    const season = seasons.find((season) => season.id === id)
    setFlexPack((prevData) => ({
      ...prevData,
      season,
    }))
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  const updateRequiredFieldErrorStatus = () => {
    setRequiredFieldErrorStatus({
      season: !flexPack.season,
      name: !flexPack?.name,
      price: isNaN(parseFloat(flexPack?.price)),
      image: !image,
      gameTickets: !flexPack?.gameTickets,
    })
  }

  const closeAlert = () => {
    setAlert({ show: false, variant: '', message: '' })
  }

  const saveInitialState = (flexPack, image, start, end) => {
    // save initial state to check whether to show save buttons
    setInitialState({
      flexPack,
      image,
      start,
      end,
    })
  }

  const handleSave = (e) => {
    e.preventDefault()
    // if error, don't save
    if (hasRequiredFieldError) {
      scrollToTop()
      return
    }

    const hasError =
      !flexPack?.season ||
      !flexPack?.name ||
      !image ||
      isNaN(parseFloat(flexPack?.price)) ||
      !flexPack?.gameTickets ||
      (flexPack?.qtyAvailable != null &&
        flexPack?.qtyAvailable !== '' &&
        (Number(flexPack.qtyAvailable) < 1 ||
          Number(flexPack.qtyAvailable) < Number(flexPack?.flexPacksSold || 0)))

    if (hasError) {
      updateRequiredFieldErrorStatus()
      setHasRequiredFieldError(hasError)
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message:
          flexPack?.qtyAvailable != null &&
          flexPack?.qtyAvailable !== '' &&
          Number(flexPack.qtyAvailable) < Number(flexPack?.flexPacksSold || 0)
            ? 'Max available cannot be less than the number of flex packages already sold.'
            : 'You are missing required subject fields. Please fill out all required fields before continuing.',
      })
      return
    }

    // if start time error
    if (endTimeError) {
      scrollToTop()
      setAlert({
        show: true,
        variant: 'danger',
        message:
          'You have input errors. Please correct all input errors before continuing.',
      })
      return
    }

    setIsSaving(true)
    const formData = new FormData()
    const hash = {}
    hash['id'] = flexPack.id
    hash['uuid'] = flexPack.uuid
    hash['name'] = flexPack.name
    hash['price'] = flexPack.price
    hash['start'] = toUtcIsoFromTimezone(start, flexPack?.timezone)
    hash['end'] = toUtcIsoFromTimezone(end, flexPack?.timezone)
    hash['description'] = flexPack.description
    hash['gameTickets'] = Number(flexPack.gameTickets)
    hash['qtyAvailable'] =
      flexPack.qtyAvailable != null && flexPack.qtyAvailable !== ''
        ? Number(flexPack.qtyAvailable)
        : null
    hash['season'] = Number(flexPack.season.id)
    hash['organization'] = organization.id
    hash['organizationUUID'] = organization.uuid
    hash['venue'] = seasons.find(
      (season) => season.id === Number(flexPack.season.id)
    )?.venue?.id // readd venue to flex pack in case season changed to a different org - mainly for Harrison's account
    hash['customFees'] = (flexPack?.customFees || [])
      .filter((fee) => fee?.id)
      .map((fee) => ({
        id: fee.id,
        name: fee.name,
        option: fee.option,
        feeAmount: fee.feeAmount,
      }))

    // adds image to flex pack
    if (image && !image.includes('https://')) {
      // Split the base64 string in data and contentType
      const block = image.split(';')
      // Get the content type of the image
      const contentType = block[0].split(':')[1] // In this case "image/gif"
      // get the real base64 content of the file
      const realData = block[1].split(',')[1] // In this case "R0lGODlhPQBEAPeoAJosM...."

      // Convert it to a blob to upload
      const blob = b64toBlob(realData, contentType)
      formData.append(`files.image`, blob)
      updateDetails(formData, hash)
    } else {
      // update with existing image
      updateDetails(formData, hash)
    }
  }

  const updateDetails = (formData, hash) => {
    formData.append('data', JSON.stringify(hash))
    createOrEditFlexPack(formData)
      .then((res) => {
        // save initial state again if editing to check whether to show save buttons
        saveInitialState(flexPack, image, start, end)
        scrollToTop()
        setAlert({
          show: true,
          variant: 'success',
          message: 'Your details have been updated.',
        })

        // update state in context
        updateFlexPack(res?.data)
        updateCanPublish(res?.data?.image?.id)
        storeFlexPackStart(res?.data?.start)
        storeFlexPackEnd(res?.data?.end)
        updateIsFlexPackOnsale(res?.data)
        updateHasFlexPackEnded(res?.data)
        updateIsFlexPackSoldout(res?.data)
        updateIsFlexPackPublished(res?.data)

        setIsSaving(false)

        if (!isEditing) {
          navigate(`/flex-package/${flexPackId}`)
        }
      })
      .catch((err) => {
        setIsSaving(false)
        console.error(err)
      })
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (isLoading) {
    return <PageLoadingContainer />
  }

  return (
    <>
      <div className="position-relative">
        <section
          className="wrapper package-form"
          data-testid="flex-pack-details-wrapper-section"
        >
          {alert.show && (
            <>
              <Alert variant={alert.variant} className="mb-5">
                <p>{alert.message}</p>
              </Alert>
            </>
          )}
          <header className="section-header section-heading section-heading--secondary">
            <h1>Flex package details</h1>
          </header>
          <Form onSubmit={handleSave}>
            <Card body className="card--md card--light">
              <BasicInfo
                requiredFieldErrorStatus={requiredFieldErrorStatus}
                seasons={seasons}
                flexPack={flexPack}
                image={image}
                setImage={setImage}
                handleChange={handleChange}
                handleSeason={handleSeason}
                setCoordinates={setCoordinates}
                coordinates={coordinates}
                handleRemove={handleRemove}
                isDisabled={hasFlexPackEnded}
                isFlexPackPublished={isFlexPackPublished}
              />
            </Card>
            <Card body className="card--md">
              <Details
                requiredFieldErrorStatus={requiredFieldErrorStatus}
                start={start}
                setStart={setStart}
                end={end}
                setEnd={setEnd}
                handleChange={handleChange}
                flexPack={flexPack}
                endTimeError={endTimeError}
                endTimeErrorMsg={endTimeErrorMsg}
                isEditable={!hasFlexPackEnded}
              />
            </Card>
            <Card body className="card--md card--light">
              <PackageCustomFee
                eventPackage={flexPack}
                organizationUUID={organization?.uuid}
                onSelect={handleCustomFees}
                isDisabled={hasFlexPackEnded}
                title="Flex package custom fees"
                description="Each fee is applied once when this flex pack is bought online, on top of the pack price. Voucher count does not change the fees."
              />
            </Card>
            {/* <Card body className="card--md">
                                <div className="card-body-heading card-body-heading--sm">
                                    <Card.Title as="h5">Flex Card preview</Card.Title>
                                </div>
                                <Preview flexPack={flexPack} image={image} color={color} setColor={setColor} handleChange={handleChange} />
                            </Card> */}
          </Form>
        </section>

        {showFooter && (
          <>
            <CreatePackageButtons
              isEditing={isEditing}
              isSaving={isSaving}
              showGoBack={!isEditing}
              handleSave={handleSave}
              handleGoBack={handleGoBack}
            />
          </>
        )}
      </div>
    </>
  )
}
